import { useState, useCallback, useMemo } from 'react'

import {
  Box,
  ButtonProps,
  Checkbox,
  ExtendButtonBase,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { NonDismissibleDialog } from '@scoir/modals'
import { ApplicantTypeLabels } from '@scoir/application-info'

import { useScid } from 'store/user/hooks'

import { AppRound } from '../types'
import CutoffPicker from './CutoffDatePicker'
import { useIsCurrentApplicationCycle, useRounds, useUpdateRound } from '../AppRoundProvider'
import DeadlinePicker from './DeadlineDatePicker'
import MaterialsDeadlineDatePicker from './MaterialsDeadlineDatePicker'
import { useRequirements } from '../RoundRequirementsProvider'

interface AppRoundModalTriggerProps extends ButtonProps {
  Component: ExtendButtonBase<any> // I want to restrict to Button / IconButton ... or extend ButtonProps
  round?: AppRound
  copy?: boolean
}

const AppRoundModalTrigger = ({ Component, round, copy, ...rest }: AppRoundModalTriggerProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <Component
        onClick={() => setIsOpen(true)}
        {...rest}
      />
      <AppRoundModal
        title={copy ? 'Copy Deadline' : round ? 'Edit Deadline' : 'Create Deadline'}
        open={isOpen}
        round={copy ? ({ ...round, id: '', name: '' } as AppRound) : round}
        onClose={() => {
          setIsOpen(false)
        }}
      />
    </>
  )
}

const { Body, Footer } = NonDismissibleDialog

interface AppRoundModalContentProps {
  round?: AppRound
  onClose: () => void
}

// TODO custom name validation -  cannot be a known name (casing and trim?)
const BindingRoundNames = ['Early Decision', 'Early Decision II']
const NonBindingRoundNames = [
  'Regular Decision',
  'Early Action',
  'Early Action II',
  'Rolling',
  'Restrictive Early Action',
  'Priority',
]
const RoundNames = [...BindingRoundNames, ...NonBindingRoundNames]
const getSelectedRoundName = (name?: string) => {
  if (!name) {
    return ''
  }
  if (RoundNames.includes(name)) {
    return name
  }
  return 'custom'
}

const isCustomNameInvalid = (name: string, allRoundNames: string[]) => {
  const compareString = name.toLowerCase().trim()

  return !![...RoundNames, 'interest', ...allRoundNames].find((protectedName) => {
    return protectedName.toLowerCase().trim() === compareString
  })
}

const AppRoundModalContent = ({ round, onClose }: AppRoundModalContentProps) => {
  const scid = useScid()
  const requirements = useRequirements()
  const rounds = useRounds()
  const isCurrentCycle = useIsCurrentApplicationCycle()
  const disableNameEdit = round?.id ? isCurrentCycle : false
  const allRoundNames = useMemo(() => rounds.map((r) => r.name), [rounds])
  const [roundName, setRoundName] = useState(getSelectedRoundName(round?.name))
  const [editRound, setEditRound] = useState<AppRound>(
    round
      ? { ...round }
      : {
          id: '',
          updatedBy: '',
          updatedAt: '',
          updatedByName: '',
          updatedBySystem: '',
          scid: scid,
          name: '',
          customType: '',
          binding: false,
          applicantType: undefined,
          cutoffDate: '',
          deadlineDate: '',
          materialsDeadlineDate: '',
          requirementsId: '',
        },
  )

  const [missingRequirements, setMissingRequirements] = useState<Record<string, boolean>>(
    round
      ? {
          name: round.name ? false : true,
        }
      : {
          name: true,
          customType: false,
          applicantType: true,
          deadlineDate: true,
          cutoffDate: true,
          requirementsId: true,
          materialsDeadlineDate: true,
        },
  )
  const updateMissingRequirements = useCallback(
    (updates) => {
      setMissingRequirements({ ...missingRequirements, ...updates })
    },
    [missingRequirements],
  )

  const [invalid, setInvalid] = useState<Record<string, string | boolean>>({})
  const updateInvalid = useCallback(
    (errorKey, errorValue) => {
      if (invalid[errorKey] !== errorValue) {
        setInvalid({
          ...invalid,
          [errorKey]: errorValue,
        })
      }
    },
    [invalid],
  )
  const isInvalid = useMemo(() => Object.keys(invalid).find((key) => !!invalid[key]), [invalid])
  const isMissingRequirements = useMemo(
    () => Object.keys(missingRequirements).find((key) => !!missingRequirements[key]),
    [missingRequirements],
  )

  const updateRound = useCallback(
    (updates, requirementUpdates?) => {
      const updatedRound = { ...editRound, ...updates }
      setEditRound(updatedRound)
      updateMissingRequirements(requirementUpdates)
    },
    [editRound, updateMissingRequirements],
  )

  const isRolling = editRound.name === 'Rolling' || editRound.customType === 'Rolling'

  const submitRound = useUpdateRound()
  const [errorMessage, setErrorMessage] = useState('')
  const [rollingOverride, setRollingOverride] = useState(!!editRound?.cutoffDate)

  const appTypeRequirements = useMemo(
    () => requirements.filter((req) => req.applicantType === editRound.applicantType),
    [requirements, editRound.applicantType],
  )

  return (
    <>
      <Body>
        <form
          id="deadline-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            setErrorMessage('')
            submitRound({
              ...editRound,
              name: editRound.name.trim(),
            })
              .then(onClose)
              .catch((errResponse) => {
                return errResponse.status === 409
                  ? 'An application round with that name already exists.'
                  : errResponse.text()
              })
              .then((errorMessage) => setErrorMessage(errorMessage))
          }}
        >
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                select
                required
                label="Round Name"
                id="name"
                value={roundName ?? ''}
                error={!!invalid.name}
                helperText={invalid.name ? 'The name must not match an existing name' : ''}
                FormHelperTextProps={{ role: 'alert' }}
                onChange={(e) => {
                  const value = e.target.value
                  let name = ''
                  let requiresCustomName = true
                  setRoundName(value)
                  if (value !== 'custom') {
                    name = value
                    requiresCustomName = false
                  }
                  const requirementChanges = {
                    ...missingRequirements,
                    name: !name,
                    requiresCustomName,
                    customType: requiresCustomName,
                  } as Record<string, string | boolean>
                  const roundChanges = {
                    ...editRound,
                    name,
                    customType: '',
                    rollingOverride: false,
                    binding:
                      value === 'custom'
                        ? editRound.binding
                        : !!BindingRoundNames.find((bindingName) => name === bindingName),
                  }
                  if (name === 'Rolling') {
                    roundChanges.deadlineDate = ''
                    roundChanges.cutoffDate = ''
                    roundChanges.materialsDeadlineDate = ''
                    requirementChanges.deadlineDate = false
                    requirementChanges.cutoffDate = false
                    requirementChanges.materialsDeadlineDate = false
                  }
                  updateRound(roundChanges, requirementChanges)
                  updateInvalid('name', allRoundNames.findIndex((rName) => rName === name) !== -1)
                }}
                variant="outlined"
                disabled={disableNameEdit}
                fullWidth
              >
                {RoundNames.map((roundType) => (
                  <MenuItem
                    value={roundType}
                    key={roundType}
                  >
                    {roundType}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Custom Name</MenuItem>
              </TextField>
            </Grid>
            {roundName === 'custom' && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    fullWidth
                    required
                    error={!!invalid.customName}
                    helperText={
                      invalid.customName
                        ? 'The custom name must not match a selectable round name or an existing name'
                        : ''
                    }
                    value={editRound.name ?? ''}
                    label="Custom Name"
                    id="custom-name"
                    disabled={disableNameEdit}
                    onChange={(e) => {
                      const name = e.target.value
                      updateRound({ name }, { name: !name, requiresCustomName: !name })
                      updateInvalid('customName', isCustomNameInvalid(name, allRoundNames))
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    select
                    required
                    label="Custom Round Type"
                    id="custom-type"
                    disabled={disableNameEdit}
                    value={editRound.customType ?? ''}
                    onChange={(e) => {
                      const customType = e.target.value
                      const requirementChanges = { customType: !customType } as Record<string, string | boolean>
                      const roundChanges = {
                        customType,
                        rollingOverride: false,
                        binding: !!BindingRoundNames.find((bindingName) => customType === bindingName),
                      } as Record<string, string | boolean>
                      if (customType === 'Rolling') {
                        roundChanges.deadlineDate = ''
                        roundChanges.cutoffDate = ''
                        roundChanges.materialsDeadlineDate = ''
                        requirementChanges.deadlineDate = false
                        requirementChanges.cutoffDate = false
                        requirementChanges.materialsDeadlineDate = false
                      }
                      updateRound(roundChanges, requirementChanges)
                    }}
                    variant="outlined"
                    fullWidth
                  >
                    {RoundNames.map((roundType) => (
                      <MenuItem
                        value={roundType}
                        key={roundType}
                      >
                        {roundType}
                      </MenuItem>
                    ))}
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                select
                required
                label="Applicant Type"
                id="applicant-type"
                value={editRound.applicantType ?? ''}
                onChange={(e) => {
                  const applicantType = e.target.value
                  updateRound(
                    { applicantType, requirementsId: '' },
                    { applicantType: !applicantType, requirementsId: true },
                  )
                }}
                variant="outlined"
                fullWidth
              >
                <MenuItem value="firstYear">{ApplicantTypeLabels.firstYear}</MenuItem>
                <MenuItem value="transfer">{ApplicantTypeLabels.transfer}</MenuItem>
              </TextField>
            </Grid>
            {(!isRolling || rollingOverride) && (
              <>
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <DeadlinePicker
                    deadline={editRound.deadlineDate}
                    cutoff={editRound.cutoffDate}
                    onUpdate={(deadlineDate) => {
                      const changes = {
                        deadlineDate,
                        cutoffDate: editRound.cutoffDate,
                        materialsDeadlineDate: editRound.materialsDeadlineDate,
                      }
                      updateRound(changes, {
                        deadlineDate: !changes.deadlineDate,
                        cutoffDate: !changes.cutoffDate,
                        materialsDeadlineDate: !changes.materialsDeadlineDate,
                      })
                    }}
                    onError={(errorValue) => {
                      updateInvalid('deadlineDate', errorValue?.props?.children ?? '')
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <CutoffPicker
                    deadline={editRound.deadlineDate}
                    cutoff={editRound.cutoffDate}
                    materialsDeadline={editRound.materialsDeadlineDate}
                    onUpdate={(cutoffDate) => {
                      updateRound({ cutoffDate }, { cutoffDate: !cutoffDate })
                    }}
                    onError={(errorValue) => {
                      updateInvalid('cutoffDate', errorValue?.props?.children ?? '')
                    }}
                  />
                </Grid>
              </>
            )}
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                select
                disabled={!editRound.applicantType || (editRound.applicantType && !appTypeRequirements.length)}
                required
                error={editRound.applicantType && !appTypeRequirements.length}
                FormHelperTextProps={{
                  role: `${editRound.applicantType && !appTypeRequirements.length ? 'alert' : ''}`,
                }}
                helperText={
                  !editRound.applicantType
                    ? 'Please select an Applicant Type'
                    : editRound.applicantType && !appTypeRequirements.length
                      ? 'There are no requirements to associate'
                      : `Options filtered to ${ApplicantTypeLabels[editRound.applicantType]}`
                }
                label="Requirements"
                id="requirements"
                value={editRound.requirementsId ?? ''}
                onChange={(e) => {
                  const requirementsId = e.target.value
                  updateRound({ requirementsId }, { requirementsId: !requirementsId })
                }}
                variant="outlined"
                fullWidth
              >
                {appTypeRequirements.map((requirement) => (
                  <MenuItem
                    key={requirement.id}
                    value={requirement.id}
                  >
                    {requirement.name ?? 'Missing Name'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {(!isRolling || rollingOverride) && (
              <Grid
                item
                xs={12}
                sm={6}
              >
                <MaterialsDeadlineDatePicker
                  cutoff={editRound.cutoffDate}
                  materialsDeadline={editRound.materialsDeadlineDate}
                  onUpdate={(materialsDeadlineDate) => {
                    updateRound({ materialsDeadlineDate }, { materialsDeadlineDate: !materialsDeadlineDate })
                  }}
                  onError={(errorValue) => {
                    updateInvalid('materialsDeadlineDate', errorValue?.props?.children ?? '')
                  }}
                />
              </Grid>
            )}
          </Grid>
          {isRolling && (
            <Box m={1}>
              <FormControlLabel
                label="Add deadline dates to this round"
                control={
                  <Checkbox
                    disableRipple={false}
                    name="override"
                    color="primary"
                    checked={rollingOverride ?? false}
                    onChange={(e) => {
                      setRollingOverride(e.target.checked)
                      const changes = {
                        deadlineDate: undefined,
                        cutoffDate: undefined,
                        materialsDeadlineDate: undefined,
                      }
                      updateRound(changes, {
                        deadlineDate: e.target.checked,
                        cutoffDate: e.target.checked,
                        materialsDeadlineDate: e.target.checked,
                      })
                    }}
                  />
                }
              />
            </Box>
          )}
          <Box my={1}>
            {editRound.binding && (
              <FormHelperText>
                Students applying to your college via this round will be required to sign an Early Decision Agreement
                and prevented from applying to other binding rounds.
              </FormHelperText>
            )}
          </Box>
        </form>
      </Body>
      {errorMessage && (
        <Box
          p={2}
          textAlign="center"
        >
          <Typography
            role="alert"
            color="error"
          >
            {errorMessage}
          </Typography>
        </Box>
      )}
      <Footer>
        <Footer.CloseButton>Cancel</Footer.CloseButton>
        <Footer.PrimaryActionButton
          form="deadline-form"
          disabled={!!isInvalid || !!isMissingRequirements}
          type="submit"
        >
          {editRound.id ? 'Save' : 'Create'}
        </Footer.PrimaryActionButton>
      </Footer>
    </>
  )
}
interface AppRoundModalProps extends AppRoundModalContentProps {
  title: string
  open: boolean
}

const AppRoundModal = ({ open, onClose, title, ...rest }: AppRoundModalProps) => (
  <NonDismissibleDialog
    open={open}
    onClose={onClose}
    scroll="paper"
    headerText={title}
  >
    <AppRoundModalContent
      onClose={onClose}
      {...rest}
    />
  </NonDismissibleDialog>
)
export default AppRoundModalTrigger
